import React, { useEffect, useState } from "react";
import { Settings, Save, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { adminService } from "../../apis/adminService";

function applyLimitsToState(limits, { setTrialDays, setMaxChartsPerDay, setMaxChartsOverTrial }) {
  if (!limits || limits.trialDays == null || limits.maxChartsPerDay == null) {
    return false;
  }
  setTrialDays(String(limits.trialDays));
  setMaxChartsPerDay(String(limits.maxChartsPerDay));
  setMaxChartsOverTrial(
    limits.maxChartsOverTrial ?? limits.trialDays * limits.maxChartsPerDay
  );
  return true;
}

export function PlatformLimitsSettings() {
  const [trialDays, setTrialDays] = useState("7");
  const [maxChartsPerDay, setMaxChartsPerDay] = useState("25");
  const [maxChartsOverTrial, setMaxChartsOverTrial] = useState(175);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadLimits = async () => {
    setLoading(true);
    try {
      const result = await adminService.getPlatformLimits();
      if (
        result.success &&
        !applyLimitsToState(result.data, {
          setTrialDays,
          setMaxChartsPerDay,
          setMaxChartsOverTrial,
        })
      ) {
        toast.error("Invalid platform limits response from server");
      } else if (!result.success) {
        toast.error(result.message || "Failed to load platform limits");
      }
    } catch {
      toast.error("Failed to load platform limits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLimits();
  }, []);

  useEffect(() => {
    const days = parseInt(trialDays, 10);
    const charts = parseInt(maxChartsPerDay, 10);
    if (Number.isFinite(days) && Number.isFinite(charts) && days > 0 && charts > 0) {
      setMaxChartsOverTrial(days * charts);
    }
  }, [trialDays, maxChartsPerDay]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await adminService.updatePlatformLimits({
        trialDays: parseInt(trialDays, 10),
        maxChartsPerDay: parseInt(maxChartsPerDay, 10),
      });
      if (result.success) {
        toast.success("Platform limits saved for all free users");
        if (
          result.data &&
          !applyLimitsToState(result.data, {
            setTrialDays,
            setMaxChartsPerDay,
            setMaxChartsOverTrial,
          })
        ) {
          await loadLimits();
        }
      } else {
        toast.error(result.message || "Failed to save platform limits");
      }
    } catch {
      toast.error("Failed to save platform limits");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-100 rounded-xl p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-600 shrink-0" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Free tier limits (all users)
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Stored in the database. Applies to every free user on the platform.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={loadLimits}
          disabled={loading || saving}
          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-white/80 rounded-lg transition-colors"
          title="Reload from database"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Trial period (days)
            </label>
            <input
              type="number"
              min={1}
              max={365}
              required
              disabled={loading || saving}
              value={trialDays}
              onChange={(e) => setTrialDays(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Charts per day
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              required
              disabled={loading || saving}
              value={maxChartsPerDay}
              onChange={(e) => setMaxChartsPerDay(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            />
          </div>
        </div>

        <p className="text-xs text-gray-600">
          Max charts over full trial:{" "}
          <span className="font-semibold text-gray-800">{maxChartsOverTrial}</span>{" "}
          ({trialDays || "?"} days × {maxChartsPerDay || "?"} charts/day)
        </p>

        <button
          type="submit"
          disabled={loading || saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-600 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save platform limits"}
        </button>
      </form>
    </div>
  );
}